/**
 * Soft deadline race for external calls that cannot be cancelled.
 *
 * `fal.subscribe` accepts no AbortSignal (as of @fal-ai/client 1.9.5), so
 * when the deadline fires the underlying promise is ABANDONED, not
 * cancelled — it keeps running in the background and its eventual result
 * is discarded. The then-handlers below stay attached either way, so a
 * late settle is always observed and never surfaces as an unhandled
 * rejection.
 *
 * Shared by the fal-backed media primitives (`smart-resize`,
 * `background-remove`) whose callers need to classify a deadline expiry
 * differently from a provider failure (a deadline means the job may still
 * be running — retrying starts a SECOND job), and by `withExternalRetry`'s
 * per-attempt `timeoutMs`, which rejects with its own `TimeoutError`
 * (`createError`).
 */

/** Thrown when the soft deadline fires before the promise settles. */
export class DeadlineExceededError extends Error {
  readonly deadlineMs: number

  constructor(label: string, deadlineMs: number) {
    super(`${label} exceeded ${deadlineMs}ms`)
    this.name = 'DeadlineExceededError'
    this.deadlineMs = deadlineMs
  }
}

export function withDeadline<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
  createError: () => Error = () => new DeadlineExceededError(label, ms)
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(createError()), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })
}
